import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import api from 'src/services/api';
import useSubscribeStorage from 'src/hooks/useSubscribeStorage';
import { DEFAULT_ENVIRONMENT_ID } from 'src/constants/storage';
import { objectToQueryString } from 'src/utils/uri';
import { APP_COUNTS_KEY } from 'src/constants/query';

const DASHBOARD_ENFORCEMENT_QUERY_KEY = 'dashboardEnforcement';
const DASHBOARD_POLICIES_QUERY_KEY = 'dashboardPolicies';
const DASHBOARD_MANAGED_INSTALLERS_QUERY_KEY = 'dashboardManagedInstallers';
const DASHBOARD_LOG_COLLECTOR_QUERY_KEY = 'dashboardLogCollectorVersions';
const DASHBOARD_CLIENT_HEALTH_QUERY_KEY = 'dashboardClientHealth';
const DASHBOARD_CLIENT_HEALTH_DEVICES_QUERY_KEY = 'dashboardClientHealthDevices';
const DASHBOARD_ENFORCEMENT_DEVICES_QUERY_KEY = 'dashboardEnforcementDevices';
const DASHBOARD_ENVIRONMENT_QUERY_KEY = 'dashboardEnvironment';

const getDashboardEnforcement = async ({ customerEnvId, query = {} }) =>
  api.get(`/dashboard/${customerEnvId}/enforcementStatusData?${objectToQueryString(query)}`);

const getDashboardPolicies = async ({ customerEnvId, page, limit, query = {} }) =>
  api.get(
    `/dashboard/${customerEnvId}/policiesChartData?${objectToQueryString({
      ...query,
      page,
      limit,
    })}`,
  );

const getDashboardManagedInstallers = async ({ customerEnvId, page, limit }) =>
  api.get(
    `/dashboard/${customerEnvId}/managedInstallerDistribution?${objectToQueryString({
      page,
      limit,
    })}`,
  );

const getDashboardLogCollectorVersions = async ({ customerEnvId, query = {} }) =>
  api.get(`/dashboard/${customerEnvId}/logCollectorVersionChart?${objectToQueryString(query)}`);

const getDashboardClientHealth = async ({ customerEnvId, query = {} }) =>
  api.get(`/dashboard/${customerEnvId}/clientHealthBar?${objectToQueryString(query)}`);

const getDashboardClientHealthDevices = async ({
  customerEnvId,
  page,
  limit,
  query = {},
  signal,
}) =>
  api.get(`/dashboard/${customerEnvId}/clientHealthDevices?${objectToQueryString(query)}`, {
    params: {
      Page: page,
      Limit: limit,
    },
    ...(signal ? { signal } : {}),
  });

const exportDashboardClientHealthDevices = async ({ customerEnvId, query = {} }) =>
  api.get(`/dashboard/${customerEnvId}/exportClientHealthDevices?${objectToQueryString(query)}`, {
    responseType: 'blob',
  });

const archiveDashboardOfflineDevices = async ({ customerEnvId, tagId }) =>
  api.post(`/dashboard/${customerEnvId}/archiveOfflineDevices`, {
    tagId,
  });

const getDashboardEnforcementDevices = async ({
  customerEnvId,
  page,
  limit,
  query = {},
  signal,
}) =>
  api.get(`/dashboard/${customerEnvId}/enforcementDevices?${objectToQueryString(query)}`, {
    params: {
      Page: page,
      Limit: limit,
    },
    ...(signal ? { signal } : {}),
  });

const getDashboardEnvironment = async ({ customerEnvId }) =>
  api.get(`/customerEnvironment/${customerEnvId}`);

export const useDashboardEnforcement = ({ apiQuery = {}, enabled = true } = {}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [DASHBOARD_ENFORCEMENT_QUERY_KEY, customerEnvId, apiQuery],
    () => getDashboardEnforcement({ customerEnvId, query: apiQuery }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
    },
  );
};

const getChartNextPageParam = (lastPage, pages) => {
  const loaded = pages.reduce((acc, page) => acc + (page.items?.length || 0), 0);
  return loaded < (lastPage.total || 0) ? pages.length + 1 : undefined;
};

export const useInfiniteDashboardPolicies = ({ apiQuery = {}, limit, enabled = true } = {}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [DASHBOARD_POLICIES_QUERY_KEY, customerEnvId, apiQuery, limit],
    ({ pageParam = 1 }) =>
      getDashboardPolicies({ customerEnvId, page: pageParam, limit, query: apiQuery }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      getNextPageParam: getChartNextPageParam,
    },
  );
};

export const useInfiniteDashboardManagedInstallers = ({ limit, enabled = true } = {}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [DASHBOARD_MANAGED_INSTALLERS_QUERY_KEY, customerEnvId, limit],
    ({ pageParam = 1 }) => getDashboardManagedInstallers({ customerEnvId, page: pageParam, limit }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      getNextPageParam: getChartNextPageParam,
    },
  );
};

export const useDashboardLogCollectorVersions = ({ apiQuery = {}, enabled = true } = {}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [DASHBOARD_LOG_COLLECTOR_QUERY_KEY, customerEnvId, apiQuery],
    () => getDashboardLogCollectorVersions({ customerEnvId, query: apiQuery }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useDashboardClientHealth = ({ apiQuery = {}, enabled = true } = {}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [DASHBOARD_CLIENT_HEALTH_QUERY_KEY, customerEnvId, apiQuery],
    () => getDashboardClientHealth({ customerEnvId, query: apiQuery }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useInfiniteDashboardClientHealthDevices = ({
  apiQuery,
  limit = 50,
  enabled = true,
}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [DASHBOARD_CLIENT_HEALTH_DEVICES_QUERY_KEY, customerEnvId, apiQuery, limit],
    ({ pageParam = 1, signal }) =>
      getDashboardClientHealthDevices({
        customerEnvId,
        page: pageParam,
        limit,
        query: apiQuery,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((acc, page) => acc + (page.devices?.length || 0), 0);
        return loaded < (lastPage.totalCount || 0) ? pages.length + 1 : undefined;
      },
    },
  );
};

export const useExportDashboardClientHealthDevices = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useMutation(({ query }) => exportDashboardClientHealthDevices({ customerEnvId, query }));
};

export const useArchiveDashboardOfflineDevices = () => {
  const queryClient = useQueryClient();
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useMutation(
    ({ tagId }) => archiveDashboardOfflineDevices({ customerEnvId, tagId }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [DASHBOARD_CLIENT_HEALTH_QUERY_KEY],
          exact: false,
        });
        await queryClient.invalidateQueries({
          queryKey: [DASHBOARD_CLIENT_HEALTH_DEVICES_QUERY_KEY],
          exact: false,
        });
        await queryClient.invalidateQueries({
          queryKey: [DASHBOARD_ENFORCEMENT_QUERY_KEY],
          exact: false,
        });
        await queryClient.invalidateQueries({
          queryKey: [DASHBOARD_ENFORCEMENT_DEVICES_QUERY_KEY],
          exact: false,
        });
        await queryClient.invalidateQueries({ queryKey: ['devices'], exact: false });
        await queryClient.invalidateQueries([APP_COUNTS_KEY, customerEnvId]);
      },
    },
  );
};

export const useInfiniteDashboardEnforcementDevices = ({
  apiQuery,
  limit = 50,
  enabled = true,
}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [DASHBOARD_ENFORCEMENT_DEVICES_QUERY_KEY, customerEnvId, apiQuery, limit],
    ({ pageParam = 1, signal }) =>
      getDashboardEnforcementDevices({
        customerEnvId,
        page: pageParam,
        limit,
        query: apiQuery,
        signal,
      }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((acc, page) => acc + (page.devices?.length || 0), 0);
        return loaded < (lastPage.total || 0) ? pages.length + 1 : undefined;
      },
    },
  );
};

export const useDashboardEnvironment = ({ enabled = true } = {}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [DASHBOARD_ENVIRONMENT_QUERY_KEY, customerEnvId],
    () => getDashboardEnvironment({ customerEnvId }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
    },
  );
};
